import {
  Body,
  Checkbox,
  Field,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Stack,
  Textarea,
  VisuallyHidden,
} from '@leanin/ui';
import styles from './IntakeFormFields.module.css';
import { useTranslations } from 'use-intl';
import { MAX_ANSWER_CHARS } from '@leanin/shared/intake';
import {
  type AnswerState,
  type AnswerValue,
  type ApplicantIntakeForm,
  type ApplicantQuestion,
} from './intake-answer-model';

type Props = {
  readonly form: ApplicantIntakeForm;
  readonly answers: AnswerState;
  readonly agreed: ReadonlySet<string>;
  readonly onAnswerChange: (questionId: string, value: AnswerValue) => void;
  readonly onAnswerBlur?: (questionId: string) => void;
  readonly onConsentToggle: (key: string, next: boolean) => void;
  /** Localized inline error for a question, or null. Computed by the caller
   *  from touched/submit state so this component stays presentational. */
  readonly errorFor: (question: ApplicantQuestion) => string | null;
  readonly consentError: string | null;
  /** Render every control non-editable (text inputs read-only, choices
   *  disabled) — used to show an already-submitted request back to the
   *  applicant. */
  readonly readOnly?: boolean;
};

// Interactive renderer of an applicant intake form: the leader-authored
// questions (shown verbatim, never machine-translated) followed by the fixed,
// required consent statements. Shared by the member join modal (A/B) and the
// Circle→Network application (C).
export function IntakeFormFields({
  form,
  answers,
  agreed,
  onAnswerChange,
  onAnswerBlur,
  onConsentToggle,
  errorFor,
  consentError,
  readOnly = false,
}: Props) {
  const t = useTranslations('Group');

  // Declarations are attestation checkboxes ("I agree to…"), so they belong
  // with the fixed consent statements at the foot of the form rather than
  // interleaved among the informational questions. Split them out; order
  // within each group is preserved. Answers are keyed by question id, so this
  // is a presentation grouping only — no data, validation, or submission
  // impact.
  const standardQuestions = form.questions.filter((q) => q.type !== 'declaration');
  const declarationQuestions = form.questions.filter((q) => q.type === 'declaration');

  return (
    <Stack gap="2xl">
      {standardQuestions.length > 0 && (
        <Stack gap="xl">
          {standardQuestions.map((q) => (
            <QuestionField
              key={q.id}
              question={q}
              value={answers[q.id]}
              error={errorFor(q)}
              readOnly={readOnly}
              onChange={(v) => onAnswerChange(q.id, v)}
              onBlur={() => onAnswerBlur?.(q.id)}
            />
          ))}
        </Stack>
      )}

      <Stack gap="lg">
        {/* Attestations group: leader-authored declarations followed by the
            fixed consent statements. The group keeps its accessible heading —
            present for assistive tech but hidden visually, matching the
            flowing design. */}
        <VisuallyHidden>
          <Label>{t('intakeForm.consent.heading')}</Label>
        </VisuallyHidden>
        {declarationQuestions.map((q) => (
          <QuestionField
            key={q.id}
            question={q}
            value={answers[q.id]}
            error={errorFor(q)}
            readOnly={readOnly}
            onChange={(v) => onAnswerChange(q.id, v)}
            onBlur={() => onAnswerBlur?.(q.id)}
          />
        ))}
        {form.consentStatementKeys.map((key) => (
          <Checkbox
            key={key}
            checked={agreed.has(key)}
            disabled={readOnly}
            required
            onCheckedChange={(next) => onConsentToggle(key, next === true)}
            label={consentStatementLabel(t, key)}
          />
        ))}
        {consentError !== null && (
          <span role="alert" aria-live="polite">
            <Body size="sm" color="destructive">{consentError}</Body>
          </span>
        )}
      </Stack>
    </Stack>
  );
}

function QuestionField({
  question,
  value,
  error,
  readOnly,
  onChange,
  onBlur,
}: {
  readonly question: ApplicantQuestion;
  readonly value: AnswerValue | undefined;
  readonly error: string | null;
  readonly readOnly: boolean;
  readonly onChange: (value: AnswerValue) => void;
  readonly onBlur: () => void;
}) {
  // A declaration is its own checkbox — the prompt is the statement, so there's
  // no separate label.
  if (question.type === 'declaration') {
    return (
      <Stack gap="xs">
        <Checkbox
          checked={value === true}
          disabled={readOnly}
          required={question.required}
          onCheckedChange={(next) => onChange(next === true)}
          label={question.prompt}
        />
        {error !== null && (
          <span role="alert" aria-live="polite">
            <Body size="sm" color="destructive">{error}</Body>
          </span>
        )}
      </Stack>
    );
  }

  const strValue = typeof value === 'string' ? value : '';

  // Long answers carry a live character count (lower-right, inside the field),
  // since they enforce a max length. Slot decomposition keeps Field's id/aria
  // injection on the Textarea itself (via Field.Control) while the positioning
  // frame around it stays purely presentational.
  if (question.type === 'long_answer') {
    return (
      <Field required={question.required} error={error ?? undefined}>
        <Field.Label>{question.prompt}</Field.Label>
        <div className={styles.textareaFrame}>
          <Field.Control>
            <Textarea
              value={strValue}
              rows={3}
              maxLength={MAX_ANSWER_CHARS}
              invalid={error !== null}
              readOnly={readOnly}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              style={{
                background: 'var(--card)',
                // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Textarea is not a layout primitive; no padding props exist on it
                padding: 'var(--space-md) var(--space-lg)',
                // Reserve a counter-height band so text never runs under it.
                // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- calc() clearance for the in-field counter; not expressible as a SpaceKey prop
                paddingBottom: 'calc(var(--space-sm) + 1.6rem)',
              }}
            />
          </Field.Control>
          <span className={styles.counter}>
            {strValue.length} / {MAX_ANSWER_CHARS}
          </span>
        </div>
      </Field>
    );
  }

  return (
    <Field
      label={question.prompt}
      required={question.required}
      error={error ?? undefined}
    >
      {question.type === 'short_answer' ? (
        <Input
          value={strValue}
          maxLength={MAX_ANSWER_CHARS}
          invalid={error !== null}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          style={{
            background: 'var(--card)',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Input is not a layout primitive; no padding props exist on it
            padding: 'var(--space-md) var(--space-lg)',
          }}
        />
      ) : (
        <RadioGroup
          value={strValue}
          disabled={readOnly}
          onValueChange={onChange}
          aria-label={question.prompt}
        >
          {(question.options ?? []).map((option, i) => (
            <RadioGroupItem key={`${option}-${i}`} value={option}>
              {option}
            </RadioGroupItem>
          ))}
        </RadioGroup>
      )}
    </Field>
  );
}

function consentStatementLabel(
  t: ReturnType<typeof useTranslations<'Group'>>,
  key: string,
): string {
  switch (key) {
    case 'share_with_leaders':
      return t('intakeForm.consent.statements.share_with_leaders');
    case 'withdraw_right':
      return t('intakeForm.consent.statements.withdraw_right');
    default:
      return key;
  }
}
